/** Investor Buy-Box Matcher (v1 investors.js). Internal team tool: investors are
 * RECORDS (not app users) with a buy-box; every property/lead is scored against
 * it for both Flip and BRRRR. The financial math reuses v2's ROI engine
 * (flipResult / brrrResult / mergeMaster) so an investor's numbers match exactly
 * what the ROI + Deals pages show. INVESTORS is an org-wide array blob written
 * through the settings write-door (Phase 22). Attribute criteria the v2 Lead
 * doesn't yet carry (HOA / pool / 55+ / structure) resolve to "unknown" and only
 * matter when the investor explicitly sets them — v1's graceful degradation. */
import { flipResult, brrrResult, mergeMaster, usd, type NrInputs } from "../roi/math";
import { dealRehabValue } from "../roi/estMoney";
import type { Lead } from "../../types";
import type { RecOp } from "../../lib/outbox";
import { withLedgerBase } from "../settings/api";

export type DealType = "flip" | "brrr" | "either";
export type Logic = "all" | "any";
export type TriState = "any" | "yes" | "no";
export type Structure = "any" | "block" | "wood" | "other";

export interface Criteria {
  yearMin: number | null; yearMax: number | null;
  priceMin: number | null; priceMax: number | null;
  hoa: TriState; s55: TriState; pool: TriState; structure: Structure;
  minProfit: number | null; minRoi: number | null;
  cocMin: number | null; cocMax: number | null; cfMin: number | null; capMin: number | null;
  cashLeftMaxPct: number | null; feMin: number | null; fePctMin: number | null;
}

export interface Investor {
  id: string;
  name: string;
  dealType: DealType;
  logic: Logic;
  criteria: Criteria;
}

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const triOf = (v: unknown): TriState => (v === "yes" || v === "no" ? v : "any");
const structOf = (v: unknown): Structure => (v === "block" || v === "wood" || v === "other" ? v : "any");

export function emptyCriteria(): Criteria {
  return {
    yearMin: null, yearMax: null, priceMin: null, priceMax: null,
    hoa: "any", s55: "any", pool: "any", structure: "any",
    minProfit: null, minRoi: null, cocMin: null, cocMax: null, cfMin: null,
    capMin: null, cashLeftMaxPct: null, feMin: null, fePctMin: null,
  };
}

export function narrowInvestor(raw: unknown): Investor {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const c = o["criteria"] && typeof o["criteria"] === "object" ? (o["criteria"] as Record<string, unknown>) : {};
  const dt = o["dealType"];
  return {
    id: typeof o["id"] === "string" && o["id"] ? o["id"] : invId(),
    name: typeof o["name"] === "string" ? o["name"] : "",
    dealType: dt === "flip" || dt === "brrr" ? dt : "either",
    logic: o["logic"] === "any" ? "any" : "all",
    criteria: {
      yearMin: numOrNull(c["yearMin"]), yearMax: numOrNull(c["yearMax"]),
      priceMin: numOrNull(c["priceMin"]), priceMax: numOrNull(c["priceMax"]),
      hoa: triOf(c["hoa"]), s55: triOf(c["s55"]), pool: triOf(c["pool"]), structure: structOf(c["structure"]),
      minProfit: numOrNull(c["minProfit"]), minRoi: numOrNull(c["minRoi"]),
      cocMin: numOrNull(c["cocMin"]), cocMax: numOrNull(c["cocMax"]), cfMin: numOrNull(c["cfMin"]),
      capMin: numOrNull(c["capMin"]), cashLeftMaxPct: numOrNull(c["cashLeftMaxPct"]),
      feMin: numOrNull(c["feMin"]), fePctMin: numOrNull(c["fePctMin"]),
    },
  };
}

export function allInvestors(orgSettings: Record<string, unknown>): Investor[] {
  const raw = orgSettings["INVESTORS"];
  return Array.isArray(raw) ? raw.map(narrowInvestor) : [];
}

let _seq = 0;
export function invId(): string {
  _seq += 1;
  return "inv" + Date.now().toString(36) + _seq.toString(36);
}

/** Whole-array replace through the settings door (v1 persistState → INVESTORS). */
export function opPutInvestors(all: Investor[]): RecOp {
  return withLedgerBase({ s: "settings", op: "put", k: "INVESTORS", d: all as unknown as Record<string, unknown>[] });
}

// ---- financials for a lead (v2 ROI engine) ---------------------------------

interface LeadFin { g: NrInputs; hasFin: boolean; }
function leadFin(lead: Lead, nrMaster: NrInputs, nrBy: unknown): LeadFin {
  const arv = lead.arv ?? lead.zest ?? 0;
  const purchase = lead.price ?? 0; // v2 lead has no separate offer; the ask is price
  const rehab = dealRehabValue(lead.address, nrBy, undefined);
  const g: NrInputs = { ...nrMaster, arv: arv || 0, purchase: purchase || 0, rehab };
  return { g, hasFin: (arv || 0) > 0 && (purchase || 0) > 0 };
}

// ---- attributes (v1 _invAttr; unknown = null) ------------------------------

interface Attr { year: number | null; price: number | null; hoa: TriState | null; pool: TriState | null; s55: TriState | null; structure: string | null; }
function leadAttr(lead: Lead): Attr {
  // v2 Lead currently carries year + price; HOA/pool/55/structure aren't scraped
  // onto the typed Lead yet, so they read as unknown (only penalize a match when
  // the investor explicitly requires them — same as v1).
  return { year: lead.yearBuilt ?? null, price: lead.price ?? null, hoa: null, pool: null, s55: null, structure: null };
}

// ---- match engine (v1 matchInvestor) ---------------------------------------

export type CheckState = "pass" | "fail" | "unknown";
export interface Check { label: string; state: CheckState; }
type Cell = [string, boolean | null, string]; // [label, cond, valueStr]

function groupState(logic: Logic, g: Cell[]): { state: "na" | "pass" | "fail" | "unknown"; checks: Cell[] } {
  if (!g.length) return { state: "na", checks: g };
  const conds = g.map((x) => x[1]);
  const anyUnknown = conds.some((x) => x === null);
  const anyPass = conds.some((x) => x === true);
  const anyFail = conds.some((x) => x === false);
  let pass: boolean | null;
  if (logic === "any") pass = anyPass ? true : anyUnknown ? null : false;
  else pass = anyFail ? false : anyUnknown ? null : true;
  return { state: pass === null ? "unknown" : pass ? "pass" : "fail", checks: g };
}

export interface MatchResult {
  status: "full" | "partial" | "none";
  hasFin: boolean;
  flip: { profit: number; roi: number };
  brrr: { coc: number; capRate: number; cfMo: number; cashLeftPct: number; forcedEquity: number; forcedEquityPct: number };
  attr: Check[];
  flipChecks: Cell[];
  brrrChecks: Cell[];
}

export function matchInvestor(inv: Investor, lead: Lead, nrMaster: NrInputs, nrBy: unknown): MatchResult {
  const c = inv.criteria;
  const a = leadAttr(lead);
  const fin = leadFin(lead, nrMaster, nrBy);
  const flip = flipResult(fin.g, "hard");
  const brrr = brrrResult(fin.g);
  const hasFin = fin.hasFin;

  const attr: Check[] = [];
  const push = (label: string, cond: boolean | null) => attr.push({ label, state: cond === null ? "unknown" : cond ? "pass" : "fail" });

  if (c.yearMin != null || c.yearMax != null) {
    if (a.year == null) push("Year built", null);
    else push(`Year ${c.yearMin ?? "…"}–${c.yearMax ?? "…"} (is ${a.year})`, (c.yearMin == null || a.year >= c.yearMin) && (c.yearMax == null || a.year <= c.yearMax));
  }
  if (c.priceMin != null || c.priceMax != null) {
    if (a.price == null) push("Price", null);
    else push(`Price ${c.priceMin != null ? usd(c.priceMin) : "…"}–${c.priceMax != null ? usd(c.priceMax) : "…"}`, (c.priceMin == null || a.price >= c.priceMin) && (c.priceMax == null || a.price <= c.priceMax));
  }
  ([["hoa", "HOA"], ["pool", "Pool"], ["s55", "55+"]] as const).forEach(([key, lbl]) => {
    const want = c[key];
    if (!want || want === "any") return;
    const have = a[key];
    push(`${lbl} = ${want}`, have == null ? null : have === want);
  });
  if (c.structure && c.structure !== "any") {
    const have = a.structure;
    let ok: boolean | null;
    if (!have) ok = null;
    else if (c.structure === "block") ok = /block|cbs|concrete|masonry|cinder/.test(have);
    else if (c.structure === "wood") ok = /wood|frame|stick/.test(have);
    else ok = !/block|cbs|concrete|masonry|cinder|wood|frame|stick/.test(have);
    push(`Structure: ${c.structure}`, ok);
  }

  const attrFail = attr.some((x) => x.state === "fail");
  const attrUnknown = attr.some((x) => x.state === "unknown");

  const cocVal = brrr.coc;
  const cocStr = cocVal === Infinity ? "∞" : cocVal.toFixed(1) + "%";
  const flipG: Cell[] = [];
  if (c.minProfit != null) flipG.push([`Profit ≥ ${usd(c.minProfit)}`, hasFin ? flip.profit >= c.minProfit : null, usd(flip.profit)]);
  if (c.minRoi != null) flipG.push([`ROI ≥ ${c.minRoi}%`, hasFin ? flip.roi >= c.minRoi : null, flip.roi.toFixed(1) + "%"]);
  const brrrG: Cell[] = [];
  const cashLeftPct = brrr.totalCashIn > 0 ? (brrr.cashLeft / brrr.totalCashIn) * 100 : 0;
  const forcedEquityPct = brrr.arv > 0 ? (brrr.forcedEquity / brrr.arv) * 100 : 0;
  if (c.cocMin != null) brrrG.push([`CoC ≥ ${c.cocMin}%`, hasFin ? cocVal >= c.cocMin : null, cocStr]);
  if (c.cocMax != null) brrrG.push([`CoC ≤ ${c.cocMax}%`, hasFin ? cocVal <= c.cocMax : null, cocStr]);
  if (c.cfMin != null) brrrG.push([`Cash flow ≥ ${usd(c.cfMin)}/mo`, hasFin ? brrr.cfMo >= c.cfMin : null, usd(brrr.cfMo) + "/mo"]);
  if (c.capMin != null) brrrG.push([`Cap rate ≥ ${c.capMin}%`, hasFin ? brrr.capRate >= c.capMin : null, brrr.capRate.toFixed(1) + "%"]);
  if (c.cashLeftMaxPct != null) brrrG.push([`Cash left ≤ ${c.cashLeftMaxPct}%`, hasFin ? cashLeftPct <= c.cashLeftMaxPct : null, cashLeftPct.toFixed(1) + "%"]);
  if (c.feMin != null) brrrG.push([`Forced equity ≥ ${usd(c.feMin)}`, hasFin ? brrr.forcedEquity >= c.feMin : null, usd(brrr.forcedEquity)]);
  if (c.fePctMin != null) brrrG.push([`Forced equity ≥ ${c.fePctMin}% of ARV`, hasFin ? forcedEquityPct >= c.fePctMin : null, forcedEquityPct.toFixed(1) + "%"]);

  const fr = groupState(inv.logic, flipG);
  const br = groupState(inv.logic, brrrG);
  let finState: "pass" | "fail" | "unknown";
  if (inv.dealType === "flip") finState = fr.state === "na" ? "pass" : fr.state;
  else if (inv.dealType === "brrr") finState = br.state === "na" ? "pass" : br.state;
  else {
    const s: string[] = [];
    if (fr.state !== "na") s.push(fr.state);
    if (br.state !== "na") s.push(br.state);
    finState = !s.length ? "pass" : s.includes("pass") ? "pass" : s.includes("unknown") ? "unknown" : "fail";
  }
  const status: MatchResult["status"] = attrFail || finState === "fail" ? "none" : attrUnknown || finState === "unknown" ? "partial" : "full";

  return {
    status, hasFin,
    flip: { profit: flip.profit, roi: flip.roi },
    brrr: { coc: cocVal, capRate: brrr.capRate, cfMo: brrr.cfMo, cashLeftPct, forcedEquity: brrr.forcedEquity, forcedEquityPct },
    attr,
    flipChecks: flipG,
    brrrChecks: brrrG,
  };
}

export const dtLabel = (dt: DealType): string => ({ flip: "Flip", brrr: "BRRRR", either: "Flip or BRRRR" }[dt] ?? "—");

/** One-line buy-box summary (v1 _invCriteriaSummary). */
export function criteriaSummary(inv: Investor): string {
  const c = inv.criteria;
  const b: string[] = [];
  if (c.yearMin != null || c.yearMax != null) b.push(`Year ${c.yearMin ?? "…"}–${c.yearMax ?? "…"}`);
  if (c.priceMin != null || c.priceMax != null) b.push(`Price ${c.priceMin != null ? usd(c.priceMin) : "…"}–${c.priceMax != null ? usd(c.priceMax) : "…"}`);
  (["hoa", "pool", "s55"] as const).forEach((k) => { if (c[k] && c[k] !== "any") b.push(`${({ hoa: "HOA", pool: "Pool", s55: "55+" } as const)[k]} ${c[k]}`); });
  if (c.structure && c.structure !== "any") b.push(c.structure);
  if (c.minProfit != null) b.push("Profit ≥" + usd(c.minProfit));
  if (c.minRoi != null) b.push("ROI ≥" + c.minRoi + "%");
  if (c.cocMin != null) b.push("CoC ≥" + c.cocMin + "%");
  if (c.cocMax != null) b.push("CoC ≤" + c.cocMax + "%");
  if (c.cfMin != null) b.push("CF ≥" + usd(c.cfMin));
  if (c.capMin != null) b.push("Cap ≥" + c.capMin + "%");
  if (c.cashLeftMaxPct != null) b.push("Cash left ≤" + c.cashLeftMaxPct + "%");
  if (c.feMin != null) b.push("FE ≥" + usd(c.feMin));
  if (c.fePctMin != null) b.push("FE ≥" + c.fePctMin + "%");
  return b.length ? b.join(" · ") : "No criteria set — matches everything";
}

export { usd };
